import type { Project } from '@/domain/types';
import { migrateAndParse } from '@/domain/format/migrate';
import { LoadReport } from '@/domain/format/validate';
import { toSerializable } from '@/domain/format/serialize';
import { createZip, fromUtf8, readZip, utf8 } from '@/util/zip';
import type { StorageProvider } from './StorageProvider';

/**
 * The `.dawproject` package.
 *
 * A project is only portable if its audio travels with it, so the package is a
 * ZIP archive containing:
 *
 *   project.json          the project document, exactly as stored locally
 *   assets/<id>.<ext>     every audio file the project references
 *   README.txt            a plain-text note explaining the format
 *
 * The archive is plain ZIP with stored (uncompressed) entries, so a musician
 * who no longer has this software — or does not trust it — can rename the file
 * to `.zip`, open it, and get their audio back. That property is the point of
 * local-first: the format is not a lock-in mechanism.
 */

export const PACKAGE_EXTENSION = '.dawproject';

const README = `This is a Stage Zero project package.

It is an ordinary ZIP archive. Rename it to .zip and open it with any archive
tool to recover your work:

  project.json    the arrangement, mixer settings and edits, as readable JSON
  assets/         every audio file the project uses, in its original format

Your audio is stored exactly as it was imported or recorded. Nothing here is
encrypted, obfuscated, or dependent on any online service.
`;

export interface ExportProgress {
  stage: 'collecting' | 'packing' | 'done';
  completed: number;
  total: number;
}

export class PackageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PackageError';
  }
}

function extensionFor(mimeType: string): string {
  switch (mimeType) {
    case 'audio/mpeg':
      return '.mp3';
    case 'audio/mp4':
      return '.m4a';
    case 'audio/ogg':
      return '.ogg';
    case 'audio/flac':
      return '.flac';
    case 'audio/webm':
      return '.webm';
    default:
      return '.wav';
  }
}

export async function exportProjectPackage(
  project: Project,
  storage: StorageProvider,
  onProgress?: (progress: ExportProgress) => void,
): Promise<Blob> {
  const assetIds = Object.keys(project.assets);
  const entries: { name: string; data: Uint8Array }[] = [];
  const missing: string[] = [];

  let completed = 0;
  for (const assetId of assetIds) {
    onProgress?.({ stage: 'collecting', completed, total: assetIds.length });
    const stored = await storage.getAsset(assetId);
    if (!stored) {
      missing.push(project.assets[assetId]?.name ?? assetId);
      completed++;
      continue;
    }
    const bytes = new Uint8Array(await stored.blob.arrayBuffer());
    entries.push({ name: `assets/${assetId}${extensionFor(stored.meta.mimeType)}`, data: bytes });
    completed++;
  }

  // A package that quietly omits audio is worse than one that refuses to build:
  // the user would discover the loss only on the machine they moved it to.
  if (missing.length > 0) {
    throw new PackageError(
      `Cannot export: audio for ${missing.length} asset(s) is missing from local storage (${missing
        .slice(0, 3)
        .join(', ')}${missing.length > 3 ? ', …' : ''}). ` +
        'This can happen if browser storage was cleared. Remove the affected clips, or re-import the audio, and try again.',
    );
  }

  onProgress?.({ stage: 'packing', completed: assetIds.length, total: assetIds.length });
  entries.unshift({ name: 'project.json', data: utf8(JSON.stringify(toSerializable(project), null, 2)) });
  entries.push({ name: 'README.txt', data: utf8(README) });

  const archive = createZip(entries);
  onProgress?.({ stage: 'done', completed: assetIds.length, total: assetIds.length });
  return new Blob([archive as BlobPart], { type: 'application/zip' });
}

export interface ImportedPackage {
  project: Project;
  warnings: string[];
}

/**
 * Reads a package and writes its audio into local storage, then returns the
 * project. Asset ids are preserved, so re-importing a package the user already
 * has simply reuses the audio already on disk rather than duplicating it.
 */
export async function importProjectPackage(
  file: Blob,
  storage: StorageProvider,
  onProgress?: (progress: ExportProgress) => void,
): Promise<ImportedPackage> {
  let entries: Map<string, Uint8Array>;
  try {
    entries = await readZip(await file.arrayBuffer());
  } catch (error) {
    throw new PackageError(
      `This file could not be read as a Stage Zero project package. ${error instanceof Error ? error.message : ''}`.trim(),
    );
  }

  const documentBytes = entries.get('project.json');
  if (!documentBytes) {
    throw new PackageError('This archive does not contain a project.json, so it is not a Stage Zero project package.');
  }

  let raw: unknown;
  try {
    raw = JSON.parse(fromUtf8(documentBytes));
  } catch {
    throw new PackageError('The project inside this package is damaged and could not be read.');
  }

  const report = new LoadReport();
  const { project } = migrateAndParse(raw, report);

  const assetIds = Object.keys(project.assets);
  let completed = 0;
  for (const assetId of assetIds) {
    onProgress?.({ stage: 'collecting', completed, total: assetIds.length });
    completed++;
    const meta = project.assets[assetId];
    if (await storage.hasAsset(assetId)) continue;

    const entry = [...entries.entries()].find(([name]) => name.startsWith(`assets/${assetId}`));
    if (!entry) {
      report.warn(`Audio for "${meta.name}" was not in the package; clips using it will be silent.`);
      continue;
    }
    await storage.putAsset(meta, new Blob([entry[1] as BlobPart], { type: meta.mimeType }));
  }

  onProgress?.({ stage: 'done', completed: assetIds.length, total: assetIds.length });
  return { project, warnings: report.warnings };
}
